import React, { useState, useEffect } from 'react';
import { RefreshCw, Eye, BarChart3, Globe, UserCheck } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import VisitorMap from './VisitorMap';

const CHART_CONTAINER_STYLE = { width: '100%', height: 300 };
const CHART_MARGIN = { top: 5, right: 20, left: 0, bottom: 5 };
const CHART_TICK_STYLE = { fontSize: 12 };
const TOOLTIP_STYLE = { borderRadius: '8px', border: '1px solid #e5e7eb' };
const BAR_RADIUS = [4, 4, 0, 0];
const tooltipFormatter = (value, name) => [value, name === 'views' ? 'Aufrufe' : 'Besucher'];
const legendFormatter = (value) => value === 'views' ? 'Aufrufe' : 'Besucher';

const AdminStatsTab = () => {
  const [analyticsOverview, setAnalyticsOverview] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [chartPeriod, setChartPeriod] = useState('week');
  const [countries, setCountries] = useState([]);
  const [popularPages, setPopularPages] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const authHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  };

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const [overviewRes, countriesRes, pagesRes] = await Promise.all([
        fetch(`${API_URL}/api/analytics/overview`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/analytics/countries`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/analytics/pages`, { headers: authHeaders() })
      ]);
      const overviewData = await overviewRes.json();
      const countriesData = await countriesRes.json();
      const pagesData = await pagesRes.json();
      setAnalyticsOverview(overviewData);
      setCountries(countriesData.countries || []);
      setPopularPages(pagesData.pages || []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchChartData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/analytics/pageviews?period=${chartPeriod}`, { headers: authHeaders() });
      const data = await response.json();
      setChartData(data.data || []);
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  useEffect(() => {
    fetchChartData();
  }, [chartPeriod]);

  if (analyticsLoading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-12 text-center">
        <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
        <p className="text-gray-500">Lade Statistiken...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Heute', views: analyticsOverview?.pageviews?.today || 0, visitors: analyticsOverview?.visitors?.today || 0, icon: Eye, color: 'text-blue-500' },
          { label: 'Diese Woche', views: analyticsOverview?.pageviews?.this_week || 0, visitors: analyticsOverview?.visitors?.this_week || 0, icon: BarChart3, color: 'text-purple-500' },
          { label: 'Dieser Monat', views: analyticsOverview?.pageviews?.this_month || 0, visitors: analyticsOverview?.visitors?.this_month || 0, icon: Globe, color: 'text-green-500' },
          { label: 'Gesamt', views: analyticsOverview?.pageviews?.total || 0, visitors: analyticsOverview?.visitors?.total || 0, icon: UserCheck, color: 'text-orange-500' },
        ].map((card) => (
          <div key={card.label} className="bg-white rounded-xl shadow-lg p-6" data-testid={`stat-card-${card.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-500">{card.label}</span>
              <card.icon className={`w-5 h-5 ${card.color}`} />
            </div>
            <p className="text-3xl font-bold text-gray-900">{card.views}</p>
            <p className="text-sm text-gray-500 mt-1">Seitenaufrufe</p>
            <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600"><strong>{card.visitors}</strong> Besucher</span>
            </div>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
          <h3 className="text-lg font-bold text-gray-900">Seitenaufrufe</h3>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            {[
              { key: 'today', label: 'Heute' },
              { key: 'week', label: 'Woche' },
              { key: 'month', label: 'Monat' },
            ].map((p) => (
              <button
                key={p.key}
                onClick={() => setChartPeriod(p.key)}
                data-testid={`chart-period-${p.key}`}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  chartPeriod === p.key ? 'bg-white shadow text-blue-700' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        {chartData.length > 0 ? (
          <div data-testid="analytics-chart" style={CHART_CONTAINER_STYLE}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={CHART_TICK_STYLE} />
                <YAxis allowDecimals={false} tick={CHART_TICK_STYLE} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={tooltipFormatter}
                />
                <Legend formatter={legendFormatter} />
                <Bar dataKey="views" fill="#3b82f6" radius={BAR_RADIUS} name="views" />
                <Bar dataKey="unique" fill="#8b5cf6" radius={BAR_RADIUS} name="unique" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-400">
            <p>Noch keine Daten fuer diesen Zeitraum</p>
          </div>
        )}
      </div>

      {/* Visitor World Map */}
      <VisitorMap countries={countries} />

      {/* Countries + Pages Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Globe className="w-4 h-4" /> Laender
            </h3>
          </div>
          {countries.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="countries-table">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50/50">
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Land</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Aufrufe</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Besucher</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {countries.map((c, i) => (
                    <tr key={i} className="hover:bg-blue-50/50 transition-colors">
                      <td className="px-5 py-3 text-sm font-medium text-gray-900">{c.country}</td>
                      <td className="px-5 py-3 text-sm text-gray-600 text-right">{c.views}</td>
                      <td className="px-5 py-3 text-sm text-gray-600 text-right">{c.visitors}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-400">Noch keine Laenderdaten</div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Eye className="w-4 h-4" /> Beliebte Seiten
            </h3>
          </div>
          {popularPages.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="pages-table">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50/50">
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Seite</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Aufrufe</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {popularPages.map((pg, i) => (
                    <tr key={i} className="hover:bg-blue-50/50 transition-colors">
                      <td className="px-5 py-3 text-sm font-medium text-gray-900">{pg.page}</td>
                      <td className="px-5 py-3 text-sm text-gray-600 text-right">{pg.views}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-400">Noch keine Seitendaten</div>
          )}
        </div>
      </div>

      <div className="text-center">
        <button
          onClick={() => { fetchAnalytics(); fetchChartData(); }}
          data-testid="refresh-analytics-btn"
          className="px-6 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors inline-flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" /> Statistiken aktualisieren
        </button>
      </div>
    </div>
  );
};

export default AdminStatsTab;
